// EL ESQUELETO de las altas cuando ocupan una ventana ANCHA —
// ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-columnas.
//
// Las pantallas de alta (subir producto, crear avatar, subir su foto, subir pose, subir
// fondo) nacieron para la columna de un sheet o del panel lateral: 420–640 de ancho, todo
// apilado, el CTA como un botón azul de ancho completo. El selector de fuentes de la caja
// del Agente mide 980 y las mostraba en una columna de 640 centrada: sobraban ~170 px de
// blanco a cada lado, el ejemplo y los tips empujaban el CTA abajo del pliegue, y cada
// pantalla resolvía su pie con un `sticky -mx-5` que no llegaba a los bordes de la
// ventana.
//
// Acá viven las tres piezas que las cuatro comparten en ese ancho, para que las cuatro se
// vean como la misma pantalla:
//
//   • `CuerpoDeAlta`   — cabecera fija (título + bajada) / cuerpo que scrollea / pie fijo.
//   • `ColumnasDeAlta` — en `md` parte el cuerpo en DOS: lo que se HACE (campos, zona de
//     subida, preview) y lo que hay que SABER (el ejemplo y los tips). Abajo de `md` es
//     una sola columna apilada, que es el teléfono y es como estaba.
//   • `AsideDeAlta`    — la columna de saber, con su título chico.
//   • `PieDeAlta`      — la barra del CTA, de borde a borde de la ventana.
//
// Nada de esto se monta si el host no pide el modo ancho: en el sheet y en el panel
// lateral las pantallas siguen exactamente como estaban.

import React from 'react';

/**
 * La ventana entera: cabecera fija con el título del alta y, debajo, lo que ponga la
 * pantalla — que es un `ColumnasDeAlta` (crece y scrollea) y, si confirma algo, un
 * `PieDeAlta` (queda fijo abajo). El host le da el alto.
 */
export const CuerpoDeAlta: React.FC<{
  titulo: string;
  bajada?: string;
  /** La pantalla pasó a un paso que trae su propio título (elegir entre los candidatos
   *  de un modelo, su preview): la cabecera del alta ahí sobra y se saca. */
  sinCabecera?: boolean;
  children: React.ReactNode;
}> = ({ titulo, bajada, sinCabecera = false, children }) => (
  <div className="flex h-full min-h-0 flex-col">
    {!sinCabecera && (
      <div className="shrink-0 px-5 pb-3 pt-4">
        <p className="text-[17px] font-semibold leading-tight text-brand-black/90">{titulo}</p>
        {bajada && (
          <p className="mt-1 max-w-[70ch] text-[13px] leading-snug text-[#3C3C43]/60">{bajada}</p>
        )}
      </div>
    )}
    {children}
  </div>
);

/**
 * El cuerpo en dos columnas. La de hacer es elástica y la de saber mide 320: el ejemplo
 * y los tips no necesitan más, y dejarla fija evita que la zona de subida se angoste en
 * las pantallas chicas de escritorio.
 */
export const ColumnasDeAlta: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="min-h-0 flex-1 overflow-y-auto px-5 pb-5"
    style={{ scrollbarWidth: 'thin' }}
  >
    {/* `md:min-h-full`: en escritorio la grilla estira para que la zona de subida
        —que pide `flex-1`— tenga contra qué crecer; sin esto quedaba un hueco blanco
        entre ella y la barra de abajo. En el teléfono NO estira: ahí las dos columnas
        se apilan y el que manda es el scroll, no el alto de la hoja. */}
    <div className="grid grid-cols-1 gap-5 md:min-h-full md:grid-cols-[minmax(0,1fr)_320px] md:gap-6">
      {children}
    </div>
  </div>
);

/** La columna de "lo que se hace". Estira a lo alto para que la zona de subida llene. */
export const ColumnaDeTrabajo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-w-0 flex-col gap-4">{children}</div>
);

/**
 * La columna de "lo que hay que saber": el ejemplo y los tips. En el teléfono va DESPUÉS
 * de la de trabajo (es el orden del DOM) porque ahí lo primero tiene que ser la acción.
 */
export const AsideDeAlta: React.FC<{ titulo: string; children: React.ReactNode }> = ({
  titulo,
  children,
}) => (
  // `self-start`: la columna de saber mide lo que mide su contenido. Estirada a todo el
  // alto de la fila, el panel gris quedaba como una barra vacía al costado.
  <aside className="flex min-w-0 flex-col gap-3 self-start rounded-2xl bg-[#F7F7F9] p-4">
    <p className="text-[12.5px] font-medium uppercase tracking-wide text-[#3C3C43]/55">
      {titulo}
    </p>
    {children}
  </aside>
);

/** Los tips del aside, con el mismo tamaño y la misma viñeta en las cuatro pantallas. */
export const TipsDeAlta: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="list-disc space-y-1.5 pl-4 text-[13px] leading-snug text-[#3C3C43]/70">
    {items.map((t, i) => (
      <li key={i}>{t}</li>
    ))}
  </ul>
);

/**
 * La barra de abajo, de borde a borde. `secundario` va a la izquierda (la salida lateral:
 * "Subir varios productos", "Cancelar") y el CTA a la derecha, que es donde lo buscan las
 * ventanas de escritorio. En el teléfono el CTA va primero y ocupa el ancho entero.
 */
export const PieDeAlta: React.FC<{
  secundario?: React.ReactNode;
  children: React.ReactNode;
}> = ({ secundario, children }) => (
  <div className="shrink-0 border-t border-black/[0.06] bg-white px-5 py-3">
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <div className="flex min-w-0 items-center gap-2">{secundario}</div>
      <div className="flex items-center gap-2 sm:justify-end">{children}</div>
    </div>
  </div>
);

/** El botón principal del pie. Ancho completo en el teléfono, a medida en escritorio. */
export const BotonPrincipalDeAlta: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled = false, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-5 py-2.5 text-[14.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
  >
    {children}
  </button>
);

/** El botón secundario del pie (la salida lateral). */
export const BotonSecundarioDeAlta: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}> = ({ onClick, disabled = false, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-[14px] font-semibold text-brand-black/80 transition-colors hover:border-brand-primary/40 hover:text-brand-black disabled:opacity-40 sm:w-auto"
  >
    {children}
  </button>
);
